import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { invStatus } from "@/components/InvoicePaper";

const fmtDate = (s) => { if (!s) return "—"; const [y, m, d] = String(s).slice(0, 10).split("-"); return d ? `${d}/${m}/${y}` : s; };

const SearchBox = ({ q, setQ, placeholder, testid }) => (
  <div className="relative w-full sm:w-80">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="rounded-none pl-9 h-9 bg-card" data-testid={testid} />
  </div>
);

export function AccountsCustomers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/accounts/customers").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  const list = useMemo(() => (rows || []).filter((c) => !q || `${c.name} ${c.company} ${c.phone} ${c.email}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  return (
    <div className="space-y-3" data-testid="accounts-customers">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <SearchBox q={q} setQ={setQ} placeholder="Search customers…" testid="acc-cust-search" />
        <Button className="rounded-none gap-1.5 font-bold uppercase" onClick={() => navigate("/dashboard/invoices/new")} data-testid="acc-new-invoice"><Plus className="h-4 w-4" /> New Invoice</Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground"><tr>
            {["Customer", "Phone", "Email", "Total Orders", "Total Billed", ""].map((h, i) => <th key={i} className={`px-4 py-2.5 ${i >= 3 && i < 5 ? "text-right" : "text-left"}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading…</td></tr>}
            {rows && list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No customers found.</td></tr>}
            {list.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`acc-cust-row-${c.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{c.company || c.name}</div>{c.company && <div className="text-xs text-muted-foreground">{c.name}</div>}</td>
                <td className="px-4 font-mono-num">{c.phone || "—"}</td>
                <td className="px-4 truncate max-w-[220px]">{c.email || "—"}</td>
                <td className="px-4 text-right font-mono-num">{c.total_orders}</td>
                <td className="px-4 text-right font-mono-num font-semibold">{money(c.total_billed)}</td>
                <td className="px-4 text-right"><Button size="sm" variant="outline" className="rounded-none h-8 text-xs gap-1" onClick={() => navigate(`/dashboard/invoices/new?customer=${c.id}`)} data-testid={`acc-cust-invoice-${c.id}`}><Plus className="h-3.5 w-3.5" /> Invoice</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AccountsInvoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [st, setSt] = useState("");
  useEffect(() => { api.get("/invoices").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  const list = useMemo(() => (rows || []).filter((i) => {
    const s = invStatus(i)[0].split(" ")[0];
    const ref = (i.lrs || []).map((x) => x.order_number).join(" ");
    return (!st || s === st) && (!q || `${i.serial} ${i.bill_to?.name} ${i.bill_to?.company} ${ref}`.toLowerCase().includes(q.toLowerCase()));
  }), [rows, q, st]);
  return (
    <div className="space-y-3" data-testid="accounts-invoices">
      <div className="flex flex-wrap gap-2 items-center">
        <SearchBox q={q} setQ={setQ} placeholder="Search invoice no, customer, order…" testid="acc-inv-search" />
        {["", "Draft", "Sent", "Paid", "Cancelled"].map((s) => (
          <button key={s || "all"} onClick={() => setSt(s)} className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${st === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`acc-inv-filter-${(s || "all").toLowerCase()}`}>{s || "All"}</button>
        ))}
        <Button className="rounded-none gap-1.5 font-bold uppercase ml-auto" onClick={() => navigate("/dashboard/invoices/new")} data-testid="acc-inv-new"><Plus className="h-4 w-4" /> New Invoice</Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground"><tr>
            {["Invoice No.", "Customer", "Order Ref.", "Amount", "GST", "Date Created", "Status", "Cancellation"].map((h, i) => <th key={h} className={`px-4 py-2.5 ${i === 3 || i === 4 ? "text-right" : "text-left"}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>}
            {rows && list.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No invoices found.</td></tr>}
            {list.map((i) => {
              const [l, cls] = invStatus(i);
              return (
                <tr key={i.id} onClick={() => navigate(`/dashboard/invoices/${i.id}`)} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40 transition-colors" data-testid={`acc-inv-row-${i.id}`}>
                  <td className="px-4 py-3 font-bold font-mono-num">{i.serial}</td>
                  <td className="px-4">{i.bill_to?.company || i.bill_to?.name || "—"}</td>
                  <td className="px-4 text-xs">{[...new Set((i.lrs || []).map((x) => x.order_number).filter(Boolean))].join(", ") || "—"}</td>
                  <td className="px-4 text-right font-mono-num font-semibold">{money(i.total)}</td>
                  <td className="px-4 text-right font-mono-num">{money(i.tax_amount || 0)}</td>
                  <td className="px-4 text-xs">{fmtDate(i.created_at || i.invoice_date)}</td>
                  <td className="px-4"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{l}</span></td>
                  <td className="px-4 text-xs max-w-[240px]" data-testid={`acc-inv-cancel-${i.id}`}>{i.status === "cancelled" ? <><div className="font-semibold text-red-600">{fmtDate(i.cancelled_at)}</div><div className="text-muted-foreground truncate" title={i.cancel_reason || ""}>{i.cancel_reason || "No reason recorded"}</div></> : <span className="text-muted-foreground">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
